import React, { useEffect, useState } from "react";
import CustomerLayout from "../../../layouts/CustomerLayout";
import {
  QuickSmsHeader,
  RecipientInput,
  MessageComposer,
  SenderIdSelector,
  SendingOptions,
} from "../../../components/quickSms";
import { toast } from "react-toastify";
import senderRequests from "../../../api/Customer/SenderRequests";
import smsRequests from "../../../api/Customer/SmsRequests";
import Loader from "../../../components/utilities/Loader";
import { motion, AnimatePresence } from "framer-motion";
import { Users, MessageSquare, Upload, ArrowRight, Send } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import subscriptionRequests from "../../../api/Customer/SubscriptionRequests";
import { updateUserUnits } from "../../../redux/userSlice";
import { useLocation, useNavigate } from "react-router-dom";

const QuickSms = () => {
  // State management
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSenders, setIsLoadingSenders] = useState(true);
  const [, setSenderIds] = useState([]);
  const [allSenderIds, setAllSenderIds] = useState([]);
  const [showFloatingSend, setShowFloatingSend] = useState(false);
  const [showFirstTimeHint, setShowFirstTimeHint] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const subscriptionUnitsLeft = user?.units;
  const dispatch = useDispatch();
  const location=useLocation()
  const navigate = useNavigate()

  // Handle scroll to show/hide floating send button
  useEffect(() => {
    const handleScroll = () => {
      const messageComposer = document.getElementById("message-composer");
      if (messageComposer) {
        const rect = messageComposer.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        const hasContent = recipients.length > 0 && message.trim().length > 0;

        // Show floating button on mobile when message composer is visible
        setShowFloatingSend(isVisible && hasContent);

        // Add visual indicator to sidebar send button on desktop
        const sidebarSendButton = document.getElementById(
          "sidebar-send-button"
        );
        if (sidebarSendButton) {
          if (isVisible && hasContent) {
            sidebarSendButton.classList.add(
              "animate-pulse",
              "ring-2",
              "ring-blue-500",
              "ring-opacity-50"
            );
          } else {
            sidebarSendButton.classList.remove(
              "animate-pulse",
              "ring-2",
              "ring-blue-500",
              "ring-opacity-50"
            );
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [recipients.length, message]);

  // Set up global keyboard shortcut handler
  useEffect(() => {
    window.handleQuickSmsSend = handleFloatingSend;

    return () => {
      delete window.handleQuickSmsSend;
    };
  }, [recipients, message, selectedSenderId]);

  // Show roadmap for first-time users
  useEffect(() => {
    const hasSeenRoadmap = localStorage.getItem("quickSmsRoadmapSeen");
    if (!hasSeenRoadmap) {
      const timer = setTimeout(() => {
        setShowRoadmap(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Show first-time hint after a delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFirstTimeHint(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchSenderIds = async () => {
      setIsLoadingSenders(true);
      try {
        // Use pagination format: page 1, high limit to get all sender IDs, no search
        const response = await senderRequests.getSenders(1, 100, "");
        const { data } = response;

        if (data?.data?.status_code === "000" && !data?.data?.in_error) {
          // For dropdown selectors, we want all sender IDs, not paginated results
          const allSenders = data?.data?.data?.data || data?.data?.data || [];
          const approvedSenderIds = allSenders.filter(
            (sender) => sender.status.toLowerCase() === "approved"
          );

          setAllSenderIds(allSenders);
          setSenderIds(approvedSenderIds);
        } else {
          toast.error(data?.data?.data?.reason || "Error fetching sender ids");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.data?.reason || "Error fetching sender ids"
        );
      } finally {
        setIsLoadingSenders(false);
      }
    };
    fetchSenderIds();
  }, []);

  // Handle adding new sender ID
  const handleAddSender = async (senderName) => {
    try {
      const response = await senderRequests.addSenderId(senderName);
      const data = response?.data
      const validationError =  response?.validationError || null;
      if(validationError) {
        toast.error(validationError);
        return;
      }

      if (data?.data?.status_code === "000" && !data?.data?.in_error) {
        // Refresh sender IDs using pagination format
        const refreshResponse = await senderRequests.getSenders(1, 100, "");
        const { data: refreshData } = refreshResponse;

        if (
          refreshData?.data?.status_code === "000" &&
          !refreshData?.data?.in_error
        ) {
          const allSenders = refreshData?.data?.data?.data || refreshData?.data?.data || [];
          const approvedSenderIds = allSenders.filter(
            (sender) => sender.status.toLowerCase() === "approved"
          );

          setAllSenderIds(allSenders);
          setSenderIds(approvedSenderIds);
        }

        toast.success("Sender ID submitted for approval!");
        return true; // Indicate success
      } else {
        toast.error(data?.data?.reason || "Failed to add sender ID");
        return false; // Indicate failure
      }
    } catch (error) {
      toast.error("An error occurred while adding the sender ID");
      console.error("Add sender error:", error);
      return false; // Indicate failure
    }
  };

  // Handle message sending
  const handleSend = async (sendData) => {
    if (!selectedSenderId) {
      toast.error("Please select a sender ID");
      return;
    }

    if (sendData.recipients.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }

    if (!sendData.message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    //* Check if user has sufficient units
    const smsPartLength = 160;
    const messageLength = sendData.message.length;
    const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
    const totalSms = sendData.recipients.length * smsParts;

    if (subscriptionUnitsLeft < totalSms) {
      toast.error(
        `Insufficient SMS units. You need ${totalSms} units but only have ${subscriptionUnitsLeft} units remaining.`
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await smsRequests.sendQuickSms({
        recipients: sendData.recipients,
        message: sendData.message,
        sender_id: selectedSenderId,
      });

      if (response.success) {
        toast.success(
          `Message sent successfully to ${sendData.recipients.length} recipient(s)!`
        );
        const unitsResponse = await subscriptionRequests.getUnitsLeft();
        if ((unitsResponse.status_code === "000" || unitsResponse.status_code === "200") && unitsResponse.data) {
          const newUnits = unitsResponse.data.units ?? unitsResponse.data.data?.units;
          if (newUnits !== undefined && newUnits !== null) {
            dispatch(updateUserUnits({ units: typeof newUnits === "number" ? newUnits : parseInt(newUnits, 10) }));
          }
        }
        setRecipients([]);
        setMessage("");
      } else {
        const errorMessage =
          response.error?.data?.reason ||
          response.error?.message ||
          "Failed to send message";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error sending message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Floating send button handler
  const handleFloatingSend = () => {
    const sendData = {
      recipients,
      message,
      totalSms: (() => {
        const smsPartLength = 160;
        const messageLength = message.length;
        const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
        return recipients.length * smsParts;
      })(),
    };
    handleSend(sendData);
  };

  // Roadmap handlers
  const handleSkipRoadmap = () => {
    setShowRoadmap(false);
    localStorage.setItem("quickSmsRoadmapSeen", "true");
  };

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowRoadmap(false);
      localStorage.setItem("quickSmsRoadmapSeen", "true");
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return {
          title: "Welcome to Quick SMS!",
          description:
            "Let's get you started with sending your first SMS message. This quick guide will walk you through the process.",
          icon: "📱",
          highlight: null,
        };
      case 2:
        return {
          title: "Step 1: Add Recipients",
          description:
            "Enter phone numbers in the recipients section. You can add multiple numbers one by one.",
          icon: "👥",
          highlight: "recipient-input",
        };
      case 3:
        return {
          title: "Step 2: Choose Sender ID",
          description:
            "Select how your recipients will see your messages. Make sure your sender ID is approved.",
          icon: "🏷️",
          highlight: "sender-selector",
        };
      case 4:
        return {
          title: "Step 3: Write Your Message",
          description:
            "Compose your message in the text area. Keep it under 160 characters to avoid multiple SMS charges.",
          icon: "✍️",
          highlight: "message-composer",
        };
      default:
        return {
          title: "Ready to Send!",
          description:
            "Once you've completed all steps, use the send button on the right sidebar or the floating button that appears.",
          icon: "🚀",
          highlight: "sidebar-send-button",
        };
    }
  };

  if (isLoadingSenders) {
    return <Loader />;
  }

  // Check if user has any units
  if (subscriptionUnitsLeft <= 0) {
    return (
      <CustomerLayout>
        <div className="px-4 pt-20 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 lg:pt-20 md:p-6">
          <div className="mx-auto max-w-4xl">
            <div className="p-8 text-center bg-white rounded-2xl border border-gray-100 shadow-xl">
              <div className="mb-6">
                <div className="flex justify-center items-center mx-auto mb-4 w-20 h-20 bg-red-100 rounded-full">
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h2 className="mb-2 text-2xl font-bold text-gray-800">
                  No SMS Units Available
                </h2>
                <p className="mb-6 text-gray-600">
                  You need to purchase a subscription plan to send SMS messages.
                </p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => (window.location.href = "/subscription-plans")}
                  className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl transition-all transform hover:from-blue-700 hover:to-purple-700 hover:scale-105"
                >
                  View Subscription Plans
                </button>

                <div className="text-sm text-gray-500">
                  <p>
                    Current balance:{" "}
                    <span className="font-semibold text-red-600">0 units</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="px-4 pt-20 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 lg:pt-20 md:p-6">
        <div className="mx-auto max-w-8xl">
          <QuickSmsHeader recipients={recipients} message={message}  />

          {(() => {
            const smsPartLength = 160;
            const messageLength = message.length;
            const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
            const totalSms = recipients.length * smsParts;
            const hasEnoughUnits = subscriptionUnitsLeft >= totalSms;

            if (recipients.length > 0 && !hasEnoughUnits) {
              return (
                <div className="p-4 mb-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <svg
                          className="w-5 h-5 text-orange-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-orange-800">
                          Insufficient Units for Current Message
                        </div>
                        <div className="text-sm text-orange-700">
                          Need {totalSms} units, have {subscriptionUnitsLeft}{" "}
                          units
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => (window.location.href = "/subscription-plans")}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg transition-colors hover:bg-orange-700"
                    >
                      Get More Units
                    </button>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="space-y-6 xl:col-span-2">
              <div id="recipient-input">
                <RecipientInput
                  recipients={recipients}
                  setRecipients={setRecipients}
                />
              </div>
              <div id="sender-selector">
                <SenderIdSelector
                  senderIds={allSenderIds}
                  selectedSenderId={selectedSenderId}
                  setSelectedSenderId={setSelectedSenderId}
                  onAddSender={handleAddSender}
                  isLoading={isLoadingSenders}
                />
              </div>
              <div id="message-composer">
                <MessageComposer
                  message={message}
                  setMessage={setMessage}
                  recipients={recipients}
                />
              </div>
            </div>

            <div className="xl:col-span-1">
              <div className="sticky top-6">
                <SendingOptions
                  recipients={recipients}
                  recipientCount={recipients.length}
                  message={message}
                  onSend={handleSend}
                  isLoading={isLoading}
                  
               
                />

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 mt-6 bg-gradient-to-r from-purple-50 to-pink-100 rounded-2xl border border-purple-200 shadow-xl"
                >
                  <h4 className="flex items-center mb-4 space-x-2 font-bold text-purple-800">
                    <MessageSquare className="w-5 h-5" />
                    <span>Quick Actions</span>
                  </h4>
                  <div className="space-y-3">
                    <button
                      onClick={() => (window.location.href = "/bulk-sms")}
                      className="flex justify-between items-center p-3 w-full bg-white rounded-xl border border-purple-200 transition-all hover:border-purple-300 hover:shadow-md"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Users className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-800">Bulk SMS</p>
                          <p className="text-xs text-gray-600">
                            Send to contact groups
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-purple-600" />
                    </button>

                    <button
                                  onClick={() => navigate("/contacts/upload", {
    state: { locationRoute: location.pathname}
  })}
                      className="flex justify-between items-center p-3 w-full bg-white rounded-xl border border-purple-200 transition-all hover:border-purple-300 hover:shadow-md"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <Upload className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-800">
                            Upload Contacts
                          </p>
                          <p className="text-xs text-gray-600">
                            Create contact groups
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-purple-600" />
                    </button>
                  </div>
                </motion.div>

                {/* Quick Tips */}
                {/* <div className="p-6 mt-6 bg-white rounded-2xl border border-gray-100 shadow-xl">
                  <h4 className="flex items-center mb-4 space-x-2 font-bold text-gray-800">
                    <span className="text-2xl">💡</span>
                    <span>Quick Tips</span>
                  </h4>
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-start space-x-2">
                      <span className="mt-1 text-blue-500">•</span>
                      <span>
                        Keep messages under 160 characters to avoid multiple SMS
                        charges
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="mt-1 text-blue-500">•</span>
                      <span>
                        Use templates for common messages to save time
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="mt-1 text-blue-500">•</span>
                      <span>
                        Make sure your sender ID is approved before sending
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="mt-1 text-blue-500">•</span>
                      <span>
                        SMS units = Number of recipients × Number of SMS parts
                        (160 characters per part)
                      </span>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* First-time User Roadmap */}
      <AnimatePresence>
        {showRoadmap && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="overflow-hidden relative mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl"
            >
              {/* Progress Bar */}
              <div className="w-full h-1 bg-gray-200">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStep / 4) * 100}%` }}
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-6 text-center">
                  <div className="mb-4 text-4xl">{getStepContent().icon}</div>
                  <h3 className="mb-2 text-xl font-bold text-gray-800">
                    {getStepContent().title}
                  </h3>
                  <p className="leading-relaxed text-gray-600">
                    {getStepContent().description}
                  </p>
                </div>

                {/* Step Indicators */}
                <div className="flex justify-center mb-6 space-x-2">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        step <= currentStep
                          ? "bg-gradient-to-r from-blue-500 to-purple-600"
                          : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center">
                  <button
                    onClick={handleSkipRoadmap}
                    className="px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
                  >
                    Skip Tutorial
                  </button>

                  <div className="flex space-x-3">
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevStep}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                      >
                        Previous
                      </button>
                    )}
                    <button
                      onClick={handleNextStep}
                      className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg transition-all transform hover:from-blue-600 hover:to-purple-700 hover:scale-105"
                    >
                      {currentStep === 4 ? "Finish" : "Next"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Highlight overlay for current step */}
              {getStepContent().highlight && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0.3) 100%)",
                  }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Send Button - Mobile */}
      <AnimatePresence>
        {showFloatingSend && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed right-6 bottom-6 z-50 lg:hidden"
          >
            <motion.button
              onClick={handleFloatingSend}
              disabled={isLoading}
              className={`flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all transform hover:scale-110 active:scale-95 ${
                isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: isLoading
                  ? "0 10px 25px rgba(0,0,0,0.2)"
                  : "0 10px 25px rgba(59, 130, 246, 0.3)",
              }}
            >
              {isLoading ? (
                <div className="w-6 h-6 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
              ) : (
                <Send className="w-6 h-6 text-white" />
              )}
            </motion.button>

            {/* Tooltip */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="absolute right-0 bottom-full px-3 py-2 mb-2 text-sm text-white whitespace-nowrap bg-gray-900 rounded-lg shadow-lg"
            >
              Send Message
              <div className="absolute right-4 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent border-t-gray-900"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Send Button - Appears when sidebar is not visible */}
      <AnimatePresence>
        {showFloatingSend && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="hidden fixed right-8 bottom-8 z-50 lg:block"
          >
            <motion.button
              onClick={handleFloatingSend}
              disabled={isLoading}
              className={`flex items-center justify-center px-6 py-4 rounded-2xl shadow-2xl transition-all transform hover:scale-105 active:scale-95 ${
                isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: isLoading
                  ? "0 10px 25px rgba(0,0,0,0.2)"
                  : "0 10px 25px rgba(59, 130, 246, 0.3)",
              }}
            >
              {isLoading ? (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                  <span className="font-semibold text-white">Sending...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Send className="w-5 h-5 text-white" />
                  <span className="font-semibold text-white">Send Message</span>
                </div>
              )}
            </motion.button>

            {/* Desktop Tooltip */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="absolute right-0 bottom-full px-4 py-2 mb-3 text-sm text-white whitespace-nowrap bg-gray-900 rounded-lg shadow-lg"
            >
              <div className="flex items-center space-x-2">
                <span>Ready to send!</span>
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              </div>
              <div className="absolute right-4 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent border-t-gray-900"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CustomerLayout>
  );
};

export default QuickSms;
